import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { postJson } from "../api";
import { formatText, useTexts } from "../texts";
import { ChoiceMenu } from "../ui/ChoiceMenu";
import { optionsNote, useServerOptions } from "../ui/serverOptions";
import { ChatIcon } from "../ui/icons/Icon";
import { EmptyState, FormActions, Notice, PageHead, useToast } from "../ui/kit";
import { Chain, PageState, ServiceState } from "../ui/pieces";
import { availabilityTone, errorText, plural, priceLine, routeName, type AiError } from "./ai";
import { ChangePriceWindow, type PricedPath } from "./ChangePriceWindow";
import { ModelPickWindow, type Candidate, type ChainKey, type ChainLink, type PickMode } from "./ModelPickWindow";
import { useAiData, useTitle } from "./parts";
import { reasoningName, ReasoningWindow } from "./ReasoningWindow";
import { RowsSkeleton, Section, useIsMobile } from "./widgets";
import styles from "./ai.module.css";

type ChainRow = {
  priority: number;
  providerModelId: number;
  providerCode: string;
  providerName: string;
  modelName: string;
  status: string;
  availability: string | null;
  priceIn: number | null;
  priceOut: number | null;
  priceCached?: number | null;
  priceCurrency: string | null;
  connectionId: number | null;
  keyName: string | null;
  keyHint: string | null;
  reasoningLevel: string | null;
  /** Niveles que admite el modelo; vacío si no deja elegir. */
  reasoningLevels: string[];
};

type ChainData = {
  route: string;
  organizationId: number | null;
  scopeName: string | null;
  usesOverride: boolean;
  chain: ChainRow[];
  candidates: Candidate[];
  keys: ChainKey[];
  overrides: { organizationId: number; name: string; primary: string | null; backups: number }[];
  embedding: {
    modelName: string | null;
    providerName: string | null;
    providerModelId: number | null;
    dimensions: number | null;
    preparing: number;
    errors: number;
    own: boolean;
    configured: boolean;
    keyName: string | null;
  } | null;
};

const routes = ["TEXT_CHAT", "VISION_CHAT", "EMBEDDING"] as const;

/** Dos iniciales de una empresa para su marca (C20). */
const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("") || "?";

/**
 * IA › Rutas (§5.9 `ia-rutas`, `ia-rutas-vision`, `ia-rutas-emb`): una pantalla para la cadena de cada ruta, con la ruta
 * y el alcance (global o una empresa) en la dirección. Cada fila, principal o respaldo, es proveedor · API key · modelo
 * (decisión del dueño, 2026-10-30). Reemplaza Asignación, Cambiar asignación, Rutas, Uso de modelos y Avanzado (H-5, D5).
 * Subir, bajar o quitar un respaldo aplica al instante con «Deshacer»; «Cambiar modelo» reemplaza la cadena y lo dice
 * (D8). Embeddings va por la vía que prepara el índice y no tiene respaldos (D11).
 */
export function RoutesPage() {
  const { L, locale } = useTexts();
  const toast = useToast();
  const mobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const route = (routes as readonly string[]).includes(params.get("route") ?? "") ? params.get("route")! : "TEXT_CHAT";
  const organizationId = Number(params.get("organizationId")) || null;
  const query = new URLSearchParams({ route, ...(organizationId ? { organizationId: String(organizationId) } : {}) });
  const { data, state, error, reload, retry } = useAiData<ChainData>(`/api/admin/ai/chain?${query}`);
  // Empresa fuera de alcance: el aviso y el selector a la vista (el selector busca en el servidor).
  const outOfScope = state === "missing" && (error?.body as AiError | null)?.messageKey === "AI_SCOPE_UNAVAILABLE";
  // R8 · AR15: el Alcance busca empresas en el servidor 50 ms después de la última tecla (nunca «las primeras 100»).
  const orgs = useServerOptions<{ organizationId: number; name: string; code: string | null }>(
    (search) => `/api/admin/organizations/page?pageSize=20${search ? `&search=${encodeURIComponent(search)}` : ""}`,
  );
  const [pick, setPick] = useState<{ mode: PickMode; preselect?: number | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  // «Cambiar precio» de un modelo de la cadena sin salir de Rutas (H2, E123).
  const [priceFor, setPriceFor] = useState<PricedPath | null>(null);
  const [reasoningFor, setReasoningFor] = useState<ChainRow | null>(null);
  const [reasoningFailure, setReasoningFailure] = useState<string | null>(null);
  useTitle(`${L("ADM_AI_ROUTES")} · ${routeName(L, route)}`);

  // C22 · mientras se prepara el índice, el estado se actualiza solo (sin botón «Actualizar», D15).
  const preparing = route === "EMBEDDING" && (data?.embedding?.preparing ?? 0) > 0;
  useEffect(() => {
    if (!preparing) return;
    const timer = window.setInterval(() => void reload(), 5000);
    return () => window.clearInterval(timer);
  }, [preparing, reload]);

  const go = (next: { route?: string; organizationId?: number | null }) => {
    const target = new URLSearchParams({ route: next.route ?? route });
    const org = next.organizationId === undefined ? organizationId : next.organizationId;
    if (org) target.set("organizationId", String(org));
    setFailure(null);
    setParams(target, { replace: next.route === undefined });
  };
  const tabHref = (code: string) =>
    `/Ai/Rutas?route=${code}${organizationId ? `&organizationId=${organizationId}` : ""}`;

  const chain = data?.chain ?? [];
  const empty = !!data && chain.length === 0 && route !== "EMBEDDING";
  const actions =
    route === "EMBEDDING" ? (
      <button
        type="button"
        className="tm-btn primary"
        disabled={!data}
        onClick={() => setPick({ mode: "embedding", preselect: data?.embedding?.providerModelId })}
      >
        {L("ADM_AI_CHANGE_MODEL")}
      </button>
    ) : empty ? (
      <button type="button" className="tm-btn primary" disabled={!data} onClick={() => setPick({ mode: "replace" })}>
        {L("ADM_CHOOSE_MODEL")}
      </button>
    ) : (
      <>
        <button
          type="button"
          className="tm-btn secondary"
          disabled={!data}
          onClick={() => setPick({ mode: "replace" })}
        >
          {L("ADM_AI_CHANGE_MODEL")}
        </button>
        <button type="button" className="tm-btn primary" disabled={!data} onClick={() => setPick({ mode: "add" })}>
          {L("ADM_AI_ADD_BACKUP")}
        </button>
      </>
    );
  const head = (
    <>
      <PageHead title={L("ADM_AI_ROUTES")} sub={L("ADM_AI_ROUTES_SUB")}>
        {mobile ? null : actions}
      </PageHead>
      <nav className={`tm-tabs ${styles.tabs}`} aria-label={L("ADM_AI_ROUTES")} data-part="pestanas">
        {routes.map((code) => (
          <a key={code} href={tabHref(code)} aria-current={code === route ? "page" : undefined}>
            {routeName(L, code)}
          </a>
        ))}
      </nav>
    </>
  );

  const scope = (
    <div className={`tm-field ${styles.scope}`}>
      <label htmlFor="route-scope">{L("ADM_AI_SCOPE")}</label>
      <ChoiceMenu
        id="route-scope"
        label={L("ADM_AI_SCOPE")}
        value={organizationId ? String(organizationId) : ""}
        options={[
          ...(orgs.query ? [] : [{ value: "", label: L("ADM_AI_GLOBAL_ALL") }]),
          ...orgs.items.map((org) => ({ value: String(org.organizationId), label: org.name })),
        ]}
        onChange={(value) => go({ organizationId: value ? Number(value) : null })}
        onSearch={orgs.search}
        note={optionsNote(L, locale, orgs)}
        currentLabel={organizationId ? (data?.scopeName ?? undefined) : L("ADM_AI_GLOBAL_ALL")}
        searchLabel={L("ADM_ORG_SEARCH")}
        clearSearchLabel={L("ADM_AI_CLEAR_SEARCH")}
        searchIcon={<ChatIcon name="search" />}
        clearIcon={<ChatIcon name="x" />}
        chevron={<ChatIcon name="chevron-down" />}
        check={<ChatIcon name="check" />}
        describedBy="route-scope-help"
        placeholder={organizationId ? L("ADM_AI_SCOPE_UNKNOWN") : undefined}
      />
      <small id="route-scope-help" className="tm-field-help">
        {L("ADM_AI_SCOPE_HELP")}
      </small>
    </div>
  );

  if (outOfScope)
    return (
      <>
        {head}
        {scope}
        <Notice tone="warning">{L("ADM_AI_SCOPE_NO_ACCESS")}</Notice>
      </>
    );
  if ((state === "failed" || state === "missing") && !data)
    return (
      <PageState
        head={head}
        title={L("ADM_UNAVAILABLE")}
        line={L("ADM_UNAVAILABLE_LINE")}
        action={
          <button type="button" className="tm-btn secondary" onClick={retry}>
            {L("ADM_RETRY")}
          </button>
        }
      />
    );

  const save = async (order: ChainLink[], message: string, previous: ChainLink[]) => {
    if (!data) return;
    setBusy(true);
    setFailure(null);
    const reply = await postJson<AiError>("/api/admin/ai/chain", {
      route,
      organizationId,
      op: "order",
      providerModelId: 0,
      order,
    });
    setBusy(false);
    if (reply.status !== 200) {
      setFailure(errorText(L, reply.body));
      return;
    }
    await reload();
    toast.show(message, {
      label: L("ADM_UNDO"),
      run: () =>
        void postJson<AiError>("/api/admin/ai/chain", {
          route,
          organizationId,
          op: "order",
          providerModelId: 0,
          order: previous,
        }).then((undo) => (undo.status === 200 ? void reload() : setFailure(errorText(L, undo.body)))),
    });
  };
  // Cada fila es un modelo con su API key: el orden y «Deshacer» viajan como esos pares.
  const links: ChainLink[] = chain.map((row) => ({
    providerModelId: row.providerModelId,
    connectionId: row.connectionId,
    reasoningLevel: row.reasoningLevel,
  }));
  const move = (from: number, to: number) => {
    const next = [...links];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item!);
    void save(next, L("ADM_CHAIN_MOVED"), links);
  };
  const remove = (index: number) =>
    void save(
      links.filter((_, position) => position !== index),
      L("ADM_CHAIN_REMOVED"),
      links,
    );
  /** El nivel de una fila, con «Deshacer» como subir, bajar o quitar. */
  const saveReasoning = async (row: ChainRow, level: string | null) => {
    const post = (reasoningLevel: string | null) =>
      postJson<AiError>("/api/admin/ai/chain", {
        route,
        organizationId,
        op: "reasoning",
        providerModelId: row.providerModelId,
        connectionId: row.connectionId,
        reasoningLevel,
      });
    setBusy(true);
    setReasoningFailure(null);
    const reply = await post(level).catch(() => ({ status: 0, body: null as AiError | null }));
    setBusy(false);
    if (reply.status !== 200) {
      setReasoningFailure(errorText(L, reply.body));
      return;
    }
    setReasoningFor(null);
    await reload();
    toast.show(formatText(L("ADM_AI_REASONING_SAVED"), row.modelName, reasoningName(L, level)), {
      label: L("ADM_UNDO"),
      run: () =>
        void post(row.reasoningLevel).then((undo) =>
          undo.status === 200 ? void reload() : setFailure(errorText(L, undo.body)),
        ),
    });
  };
  const scopeText = data?.scopeName ?? L("ADM_AI_GLOBAL");
  const emb = data?.embedding;

  return (
    <>
      {head}
      {scope}
      {failure ? <Notice tone="error">{failure}</Notice> : null}
      {!data ? (
        <RowsSkeleton rows={2} />
      ) : route === "EMBEDDING" ? (
        <>
          {emb && emb.configured ? (
            <dl className={styles.facts}>
              <div>
                <dt>{L("ADM_AI_MODEL")}</dt>
                <dd>{[emb.modelName, emb.providerName].filter(Boolean).join(" · ")}</dd>
              </div>
              <div>
                <dt>{L("ADM_AI_API_KEY")}</dt>
                <dd>{emb.keyName ?? "—"}</dd>
              </div>
              <div>
                <dt>{L("ADM_AI_DIMENSIONS")}</dt>
                <dd>{emb.dimensions == null ? "—" : emb.dimensions.toLocaleString(locale)}</dd>
              </div>
              <div>
                <dt>{L("ADM_AI_INDEX")}</dt>
                <dd>
                  {emb.preparing > 0
                    ? plural(L, locale, emb.preparing, "ADM_AI_INDEX_PREPARING_ONE", "ADM_AI_INDEX_PREPARING")
                    : L("ADM_AI_INDEX_READY")}
                </dd>
              </div>
              <div>
                <dt>{L("ADM_AI_INDEX_ERRORS")}</dt>
                <dd>{emb.errors.toLocaleString(locale)}</dd>
              </div>
            </dl>
          ) : (
            <EmptyState
              title={L("ADM_AI_NO_MODEL")}
              line={L("ADM_AI_EMB_EMPTY_LINE")}
              action={
                <button type="button" className="tm-btn secondary" onClick={() => setPick({ mode: "embedding" })}>
                  {L("ADM_CHOOSE_MODEL")}
                </button>
              }
            />
          )}
          <Notice tone="info">{L("ADM_AI_EMB_CHANGE_NOTE")}</Notice>
          <p className={styles.note}>{L("ADM_AI_EMB_NO_BACKUPS")}</p>
        </>
      ) : empty ? (
        <>
          <Notice tone="warning">{formatText(L(`ADM_AI_ROUTE_EMPTY_${route}`), scopeText)}</Notice>
          <EmptyState
            title={L("ADM_AI_NO_MODEL")}
            line={L("ADM_AI_NO_MODEL_LINE")}
            action={
              <button type="button" className="tm-btn secondary" onClick={() => setPick({ mode: "replace" })}>
                {L("ADM_CHOOSE_MODEL")}
              </button>
            }
          />
        </>
      ) : (
        <>
          {organizationId && !data.usesOverride ? <p className={styles.note}>{L("ADM_AI_INHERITS_GLOBAL")}</p> : null}
          <div className={styles.chainWide}>
            <Chain
              busy={busy}
              rows={chain.map((row) => {
                const tone = availabilityTone(L, row.availability);
                // C24 1.14.11 · el precio completo de la fila, con la entrada en caché.
                const price = priceLine(
                  L,
                  locale,
                  { input: row.priceIn, cached: row.priceCached, output: row.priceOut },
                  row.priceCurrency,
                );
                // Proveedor · API key · precio: la API key decide con qué cuenta responde la fila.
                const key = row.keyName ? formatText(L("ADM_AI_KEY_NAMED"), row.keyName) : L("ADM_AI_KEY_ANY_ACTIVE");
                const where = `${row.providerName || row.providerCode} · ${key} · `;
                // Solo los modelos que dejan elegir dicen cuánto razonan.
                const canReason = (row.reasoningLevels ?? []).length > 0;
                const changeReasoning = () => {
                  setReasoningFailure(null);
                  setReasoningFor(row);
                };
                const reasoning = canReason ? (
                  <>
                    {" · "}
                    <span data-part="razonamiento">
                      {row.reasoningLevel
                        ? formatText(L("ADM_AI_REASONING_IS"), reasoningName(L, row.reasoningLevel))
                        : reasoningName(L, null)}
                    </span>
                    {mobile ? null : (
                      <button
                        type="button"
                        className={`tm-link ${styles.inlineAction}`}
                        data-part="cambiar-razonamiento"
                        onClick={changeReasoning}
                      >
                        {L("ADM_AI_REASONING_CHANGE")}
                      </button>
                    )}
                  </>
                ) : null;
                // Todo modelo de una cadena está en uso: sin precio, «Sin precio» en ocre y su salida (C24, C28 1.14.12).
                const changePrice = () =>
                  setPriceFor({
                    providerModelId: row.providerModelId,
                    providerName: row.providerName || row.providerCode,
                    modelName: row.modelName,
                    inputPrice: row.priceIn,
                    cachedPrice: row.priceCached ?? null,
                    outputPrice: row.priceOut,
                  });
                return {
                  key: `${row.providerModelId}-${row.connectionId ?? "any"}`,
                  name: row.modelName,
                  state: <ServiceState tone={tone.tone}>{tone.text}</ServiceState>,
                  line: price ? (
                    <span className={styles.prettyLine}>
                      {`${where}${price}`}
                      {reasoning}
                    </span>
                  ) : (
                    <>
                      {where}
                      <span className={styles.noPrice} data-part="sin-precio">
                        {L("ADM_AI_NO_PRICE_CAP")}
                      </span>
                      {mobile ? null : (
                        <button
                          type="button"
                          className={`tm-link ${styles.inlineAction}`}
                          data-part="cambiar-precio"
                          onClick={changePrice}
                        >
                          {L("ADM_AI_CHANGE_PRICE")}
                        </button>
                      )}
                      {reasoning}
                    </>
                  ),
                  actions: [
                    ...(price ? [] : [{ label: L("ADM_AI_CHANGE_PRICE"), icon: "tag" as const, run: changePrice }]),
                    ...(canReason
                      ? [{ label: L("ADM_AI_REASONING_CHANGE"), icon: "settings" as const, run: changeReasoning }]
                      : []),
                  ],
                };
              })}
              onMove={move}
              onRemove={remove}
            />
          </div>
          <p className={styles.note}>{L(mobile ? "ADM_AI_CHAIN_HELP_MOBILE" : "ADM_AI_CHAIN_HELP")}</p>
          {!organizationId && data.overrides.length > 0 ? (
            <Section
              id="route-overrides"
              title={L("ADM_AI_OWN_CHAINS")}
              line={plural(L, locale, data.overrides.length, "ADM_AI_OWN_CHAINS_LINE_ONE", "ADM_AI_OWN_CHAINS_LINE")}
            >
              <ul className={styles.list}>
                {(showAll ? data.overrides : data.overrides.slice(0, 5)).map((item) => (
                  <li key={item.organizationId} className={styles.row}>
                    <span className={styles.mark} aria-hidden="true">
                      {initials(item.name)}
                    </span>
                    <span className={styles.rowText}>
                      <span className={styles.line1}>
                        <strong>{item.name}</strong>
                        <span className={styles.tagAttention}>{L("ADM_AI_OWN")}</span>
                      </span>
                      <span className={styles.line2}>
                        {formatText(L("ADM_AI_PRIMARY_IS"), item.primary ?? "—")} ·{" "}
                        {item.backups === 0
                          ? L("ADM_AI_NO_BACKUP")
                          : plural(L, locale, item.backups, "ADM_AI_N_BACKUPS_ONE", "ADM_AI_N_BACKUPS")}
                      </span>
                    </span>
                    <span className={styles.rowEnd}>
                      <button
                        type="button"
                        className="tm-btn secondary"
                        onClick={() => go({ organizationId: item.organizationId })}
                      >
                        {L("ADM_AI_SEE_CHAIN")}
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
              {data.overrides.length > 5 && !showAll ? (
                <button type="button" className="tm-btn secondary" onClick={() => setShowAll(true)}>
                  {formatText(L("ADM_AI_SEE_ALL_N"), data.overrides.length.toLocaleString(locale))}
                </button>
              ) : null}
            </Section>
          ) : null}
        </>
      )}
      {mobile && data ? (
        <div className={styles.mobileOnly}>
          <FormActions>{actions}</FormActions>
        </div>
      ) : null}
      {priceFor ? (
        <ChangePriceWindow
          path={priceFor}
          onClose={() => setPriceFor(null)}
          onSaved={() => {
            setPriceFor(null);
            void reload();
          }}
        />
      ) : null}
      {reasoningFor ? (
        <ReasoningWindow
          modelName={reasoningFor.modelName}
          providerName={reasoningFor.providerName || reasoningFor.providerCode}
          level={reasoningFor.reasoningLevel}
          levels={reasoningFor.reasoningLevels}
          busy={busy}
          failure={reasoningFailure}
          onClose={() => setReasoningFor(null)}
          onSave={(level) => void saveReasoning(reasoningFor, level)}
        />
      ) : null}
      {pick ? (
        <ModelPickWindow
          open
          mode={pick.mode}
          route={route}
          organizationId={organizationId}
          scopeName={data?.scopeName ?? null}
          inChain={links}
          preselect={pick.preselect}
          onClose={() => setPick(null)}
          onDone={() => {
            setPick(null);
            void reload();
          }}
        />
      ) : null}
    </>
  );
}
